<template>
  <Banner 
    v-if="view === 'wizard'" 
    :customer="customer" 
    :hasSubscription="hasSubscription"
  />
  <div 
    class="subscribe" 
    :class="{'blur': customer.isVerified === 'false'}"
    :inert="customer.isVerified === 'false'" >
    <div class="container">
      <template v-if="view === 'wizard'">
        <h2 class="subscribe__header h4" v-if="hasSubscription">{{ t('hasSubscriptionHeader') }}</h2>
        <h2 class="subscribe__header h4" v-else>{{ t('header') }}</h2>
        <div class="subscribe__wrapper">
          <div class="subscribe__steps">
            <SubscribeStep1
              data-step="1"
              :isOpen="activeStep === 1"
              :products="products"
              :productKey="productKey"
              :getQty="getQty"
              :canAddMore="canAddMore"
              :isDone="canOpenStep2"
              :getSubscriptionQty="getSubscriptionQty"
              :warning="isSubscribeDisabled ? t('disabledWarning') : ''"
              :disabled="isSubscribeDisabled"
              @toggle="toggleStep(1)"
              @add="addBlock"
              @openProductModal="openProductModal"
              @view-all="openAllView"
            />

            <SubscribeStep2
              ref="step2Ref"
              data-step="2"
              :isOpen="activeStep === 2"
              :disabled="isSubscribeDisabled || !canOpenStep2"
              :isDone="canOpenStep3"
              :hasSubscription="hasSubscription"
              :isLoading="isLoading"
              :subscription="subscriptionData?.data?.subscription"
              v-model:startDate="startDate"
              v-model:periodMonths="periodMonths"
              @toggle="toggleStep(2)"
            />

            <SubscribeStep3
              ref="step3Ref"
              data-step="3"
              :isOpen="activeStep === 3"
              :disabled="isSubscribeDisabled || !canOpenStep3"
              :isLoading="isLoading"
              :customer="customer"
              v-model:delivery="step3Delivery"
              v-model:payType="step3PayType"
              :isLiqPayEnabled="isLiqPayEnabled"
              v-model:phone="step3Phone"
              v-model:note="step3Note"
              v-model:notifyDays="step3NotifyDays"
              v-model:agreement="step3Agreement"
              @toggle="toggleStep(3)"
            />
            <a :href="subscriptionRules" class="subscribe__link">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.99935 14.2077C4.57235 14.2077 1.79422 11.4296 1.79422 8.0026C1.79422 4.57561 4.57235 1.79747 7.99935 1.79747C11.4263 1.79747 14.2045 4.57561 14.2045 8.0026C14.2045 11.4296 11.4263 14.2077 7.99935 14.2077ZM0.666016 8.0026C0.666016 12.0527 3.94926 15.3359 7.99935 15.3359C12.0494 15.3359 15.3327 12.0527 15.3327 8.0026C15.3327 3.95252 12.0494 0.669271 7.99935 0.669271C3.94926 0.669271 0.666016 3.95252 0.666016 8.0026ZM7.99935 12.5154C8.31089 12.5154 8.56345 12.2629 8.56345 11.9513V6.8744C8.56345 6.56285 8.31089 6.3103 7.99935 6.3103C7.6878 6.3103 7.43525 6.56285 7.43525 6.8744V11.9513C7.43525 12.2629 7.6878 12.5154 7.99935 12.5154ZM7.99935 3.77184C8.46667 3.77184 8.8455 4.15067 8.8455 4.61799C8.8455 5.08531 8.46667 5.46414 7.99935 5.46414C7.53203 5.46414 7.1532 5.08531 7.1532 4.61799C7.1532 4.15067 7.53203 3.77184 7.99935 3.77184Z" fill="currentColor"/>
              </svg>
              <span>{{t('subscriptionRulesLabel')}}</span>
            </a>
          </div>

          <SubscribeRightPanel
            :items="selectedProducts"
            :selectedCount="selectedCount"
            :maxBlocks="MAX_BLOCKS"
            :progressPercent="progressPercent"
            :totalSum="totalSum"
            :canAddMore="canAddMore"
            :canGoNextBtn="canGoNextBtn"
            :activeStep="activeStep"
            :isLoading="isLoading"
            :getSubscriptionQty="getSubscriptionQty"
            :hasSubscription="hasSubscription"
            :disabled="isSubscribeDisabled"
            @increase="addBlock"
            @decrease="removeBlock"
            @clear-item="clearProduct"
            @clear-all="clearAll"
            @next="onNextClick"
          />
        </div>
      </template>

      <div class="subscribe__wrapper subscribe__wrapper--all" v-else>
        <div class="subscribe__all">
          <button type="button" class="subscribe__all-back" @click="backToWizard">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M20.0004 12.0273C20.0004 11.5707 19.6298 11.2002 19.1732 11.2002L6.04041 11.2002L10.6469 6.3994C10.963 6.06993 10.9519 5.5466 10.6224 5.23045C10.293 4.91438 9.76963 4.92451 9.45349 5.25389L3.52966 11.4277C3.22308 11.7475 3.22308 12.2524 3.52966 12.5722L9.45349 18.7461C9.76963 19.0755 10.293 19.0856 10.6224 18.7695C10.9519 18.4534 10.963 17.93 10.6469 17.6006L6.09216 12.8535L19.1732 12.8535C19.6297 12.8535 20.0002 12.4838 20.0004 12.0273Z" fill="currentColor"/>
            </svg>
            <span>Назад</span>
          </button>

          <div class="subscribe__all-header h4">
            {{t('viewAllHeader')}}
          </div>

          <div class="subscribe__all-tabs">
            <div class="subscribe__all-tabs__buttons">
              <div class="select">
                <select
                  id="product-filter"
                  name="product-filter"
                  v-model="activeTab"
                  ref="selectEl"
                >
                  <option
                    v-for="tab in tabs"
                    :key="tab.value"
                    :value="tab.value"
                  >
                    {{ tab.label }}
                  </option>
                </select>

                <div class="select__icon select__icon--arrow" role="presentation">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                      d="M26.3974 10.9363C26.7565 11.2954 26.7565 11.8776 26.3974 12.2367L17.5698 21.0643C17.3974 21.2368 17.1635 21.3337 16.9196 21.3337C16.6757 21.3337 16.4419 21.2368 16.2694 21.0643L7.44182 12.2367C7.08272 11.8776 7.08272 11.2954 7.44182 10.9363C7.80093 10.5772 8.38315 10.5772 8.74225 10.9363L16.9196 19.1137L25.097 10.9363C25.4561 10.5772 26.0383 10.5772 26.3974 10.9363Z"
                      fill="#2A2C2C"
                    />
                  </svg>
                </div>
              </div>

              <button
                v-for="tab in tabs"
                :key="tab.value"
                :class="['subscribe__all-tabs__btn', { active: activeTab === tab.value }]"
                @click="activeTab = tab.value"
              >
                {{ tab.label }}
              </button>
            </div>

            <div class="subscribe__all-wrapper">
              <div class="subscribe__all-items">
                <div
                  v-for="product in filteredProducts"
                  :key="product.product_id"
                  class="card"
                >
                  <ProductCard
                    :product="product"
                    :qty="getQty(product)"
                    :subscriptionQty="getSubscriptionQty(product)"
                    :canAddMore="canAddMore"
                    :activeTipKey="activeViewAllTipKey"
                    currencyLabel="грн"
                    @add="addBlock(product)"
                    @openProductModal="openProductModal(product)"
                    @cant-add="onViewAllCantAdd"
                    @dismiss-tip="dismissViewAllTip"
                  />
                </div>
              </div>

              <SubscribeRightPanel
                :items="selectedProducts"
                :selectedCount="selectedCount"
                :maxBlocks="MAX_BLOCKS"
                :progressPercent="progressPercent"
                :totalSum="totalSum"
                :canAddMore="canAddMore"
                :canGoNextBtn="canGoNextBtn"
                :activeStep="activeStep"
                :isLoading="isLoading"
                :getSubscriptionQty="getSubscriptionQty"
                @increase="addBlock"
                @decrease="removeBlock"
                @clear-item="clearProduct"
                @clear-all="clearAll"
                @next="onNextClick"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ProductModal
      :open="isProductModalOpen"
      :product="modalProduct"
      :can-add-more="canAddMore"
      :qty="modalProduct ? getQty(modalProduct) : 0"
      :subscriptionQty="modalProduct ? getSubscriptionQty(modalProduct) : 0"
      @close="closeProductModal"
      @add="onModalAdd"
    />
  </div>

  <div class="subscribe-not-autorize__popup" v-if="customer.isVerified === 'false'">
    <div class="subscribe-not-autorize__popup-text h6">{{ t('popupNotAuthorizeText') }}</div>
    <a 
      :href="buttonNotVerifiedLink"  
      class="subscribe-not-autorize__popup-btn btn btn--orange"
      v-if="customer.customerStatus == 'auth'"
    >
      {{ t('popupNotVerifeidBtnLabel') }}
    </a>
    <a  
      :href="buttonNotAutorizeLink" 
      class="subscribe-not-autorize__popup-btn btn btn--orange lox"
      v-else
    >
      {{ t('popupNotAuthorizeBtnLabel') }}
    </a>
  </div>

  <SuccessModal
    :open="isSuccessModalOpen"
    :actionType="actionType"
    @close="closeSuccessModal"
  />
<!--render from legal.js and legal.liquid-->
<!--  <div class="simple-text section section&#45;&#45;dark">-->
<!--    <div class="container">-->
<!--      <div class="simple-text__main" v-html="t('disclaimer')"></div>-->
<!--    </div>-->
<!--  </div>-->
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import TomSelect from 'tom-select'

import Banner from './components/Banner.vue'
import SubscribeStep1 from './components/SubscribeStep1.vue'
import SubscribeStep2 from './components/SubscribeStep2.vue'
import SubscribeStep3 from './components/SubscribeStep3.vue'
import SuccessModal from './components/SuccessModal.vue'
import SubscribeRightPanel from './components/SubscribeRightPanel.vue'
import ProductCard from './components/ProductCard.vue'
import ProductModal from './components/ProductModal.vue'
import { useSubscription } from './composables/useSubscription'
import { normalizePhoneDigits } from '@/js/autorization/phone.utils'

const i18n = {
  uk: {
    header: 'Налаштуй свою підписку',
    hasSubscriptionHeader: 'Редагувати підписку',
    subscriptionRulesLabel: 'Правила Підписки',
    viewAllHeader: 'Обери блоки стіків',
    popupNotAuthorizeText: 'Підписка доступна для зареєстрованих користувачів, що підтвердили свій вік',
    popupNotAuthorizeBtnLabel: 'Увійти або Зареєструватися',
    popupNotVerifeidBtnLabel: 'Верифікуватись',
    disabledWarning: 'Оформлення та відправлення замовлень тимчасово недоступно через пошкодження складу внаслідок обстрілу 20.08.2026.<br><span class="subscribe__step-warning-note">Перепрошуємо за незручності та дякуємо за розуміння. Знайти найближчу точку продажу можна на сторінці <a href="/pages/where-to-buy">«Точки продажу»</a>.</span>',
    disclaimer: '¹ Дата початку комплектації та відправки замовлення в рамках цього та наступних місяців дії підписки. <br>² Доставка здійснюється по всій Україні, окрім територій, які у зв’язку з воєнною агресією рф окуповані.'
  },
  ru: {
    header: 'Настрой свою подписку',
    hasSubscriptionHeader: 'Изменить подписку',
    subscriptionRulesLabel: 'Правила подписки',
    viewAllHeader: 'Выберите блоки стиков',
    popupNotAuthorizeText: 'Подписка доступна для зарегистрированных пользователей, подтвердивших свой возраст',
    popupNotAuthorizeBtnLabel: 'Войти или Зарегистрироваться',
    popupNotVerifeidBtnLabel: 'Верифицироваться',
    disabledWarning: 'Оформление и отправка заказов временно недоступны из-за повреждения склада вследствие обстрела 20.08.2026.<br><span class="subscribe__step-warning-note">Приносим извинения за неудобства и благодарим за понимание. Найти ближайшую точку продажи можно на странице <a href="/pages/where-to-buy">«Точки продажи»</a>.</span>',
    disclaimer: '¹ Дата начала комплектации и отправки заказа в рамках этого и последующих месяцев действия подписки. <br>² Доставка осуществляется по всей Украине, кроме территорий, которые в связи с военной агрессией рф оккупированы. '
  },
}

const locale = computed(() => window.Shopify?.locale || 'uk')
const t = (key) => i18n[locale.value]?.[key] || key

const buttonNotAutorizeLink = computed(() =>
  locale.value === 'ru'
    ? '/ru/account/login?return_to=/pages/subscription'
    : '/account/login?return_to=/pages/subscription'
)

const buttonNotVerifiedLink = computed(() =>
  locale.value === 'ru' ? '/ru/pages/age-verification' : '/pages/age-verification'
)

const subscriptionRules = computed(() =>
  locale.value === 'ru' ? '/ru/account#subscription:rules' : '/account#subscription:rules'
)

const step2Ref = ref(null)
const step3Ref = ref(null)
const actionType = ref('create')

const { customer } = defineProps({
  customer: { type: Object, required: true },
})

const { isLoading, subscriptionData, init, submit } = useSubscription(customer)

const view = ref('wizard')
const openAllView = () => { view.value = 'all' }
const backToWizard = () => { view.value = 'wizard' }

const activeViewAllTipKey = ref(null)

const onViewAllCantAdd = (tipKey) => {
  activeViewAllTipKey.value = tipKey
}

const dismissViewAllTip = (tipKey) => {
  if (!tipKey || activeViewAllTipKey.value === tipKey) activeViewAllTipKey.value = null
}

const activeTab = ref('all')

const tabs = [
  { label: 'Усі стіки', value: 'all' },
  { label: 'Neo', value: 'neo' },
  { label: 'Lucky Strike Sticks', value: 'lucky' },
  { label: 'KENT Sticks', value: 'kent' }
]

const selectEl = ref(null)
let tom = null

const destroyTom = () => {
  if (!tom) return
  tom.destroy()
  tom = null
}

const initTom = async () => {
  await nextTick()

  if (!selectEl.value || tom) return

  tom = new TomSelect(selectEl.value, {
    create: false,
    allowEmptyOption: false,
    onChange(value) {
      activeTab.value = value
    }
  })

  if (tom.getValue() !== activeTab.value) {
    tom.setValue(activeTab.value, true)
  }
}

watch(view, async (v) => {
  if (v === 'all') await initTom()
  else destroyTom()
})

watch(activeTab, (val) => {
  if (tom && tom.getValue() !== val) tom.setValue(val, true)
})

onBeforeUnmount(() => {
  destroyTom()
})

const products = ref([])

const filteredProducts = computed(() => {
  const list = products.value || []
  if (activeTab.value === 'all') return list

  return list.filter((product) => {
    const title = (product.product_title || '').toLowerCase()

    if (activeTab.value === 'neo') return title.includes('neo')
    if (activeTab.value === 'lucky') return title.includes('lucky')
    if (activeTab.value === 'kent') return title.includes('kent')

    return true
  })
})

const createDefaultDelivery = () => ({
  cityLabel: '',
  city: '',
  province: '',
  type: 1,
  deliveryTypeId: 'np_delivery_type_department',
  deliveryCityId: '',
  fields: {
    np_department: '',
    np_department_label: '',
  },
})

const isLiqPayEnabled = Boolean(window.themeSettings?.liqpayPayment)
const LIQPAY_PAY_TYPE = '2'
const COD_PAY_TYPE = '1'
const DEFAULT_PAY_TYPE = isLiqPayEnabled ? LIQPAY_PAY_TYPE : COD_PAY_TYPE

function normalizePayType(value) {
  const payType = String(value ?? '')
  return !isLiqPayEnabled && payType === LIQPAY_PAY_TYPE ? COD_PAY_TYPE : payType
}

const createDefaultStep3State = () => ({
  delivery: createDefaultDelivery(),
  payType: DEFAULT_PAY_TYPE,
  phone: '',
  note: '',
  notifyDays: '1',
  agreement: false,
})

function normalizeIsoDate(any) {
  const s = String(any || '').trim()
  if (!s) return ''
  return s.slice(0, 10)
}

function createStep3StateFromSubscription(sub) {
  const city = String(sub?.json?.np_city || '')
  const province = String(sub?.json?.np_province || '')
  const cityLabel = city && province ? `${city} (${province})` : city
  return {
    delivery: {
      cityLabel,
      city,
      province,
      type: Number(sub?.json?.address_type || 1),
      deliveryTypeId: String(sub?.json?.np_delivery_type || 'np_delivery_type_department'),
      deliveryCityId: String(sub?.json?.delivery_city_id || ''),
      fields: {
        np_department: String(sub?.json?.np_department || ''),
        np_department_label: String(sub?.json?.np_department || ''),
      },
    },
    payType: sub?.pay_type != null ? normalizePayType(sub.pay_type) : '',
    phone: String(sub?.phone || ''),
    note: String(sub?.note || ''),
    notifyDays: sub?.notify_days != null ? String(sub.notify_days) : '',
    agreement: false,
  }
}

const step3State = ref(createDefaultStep3State())

const startDate = ref('')
const periodMonths = ref('')

const hasSubscription = computed(() => {
  const r = subscriptionData.value
  const data = r?.data
  const sub = data?.subscription
  return !!(data?.exists || Number(sub?.active) === 1 || sub?.id)
})

const isNewSubscriptionBlocked = ref(Boolean(window.themeSettings?.blockOrder))
const isSubscribeDisabled = computed(() => isNewSubscriptionBlocked.value && !hasSubscription.value)

const subscriptionProducts = computed(() => {
  const sub = subscriptionData.value?.data?.subscription
  return Array.isArray(sub?.products) ? sub.products : []
})

watch(
  () => [isLoading.value, subscriptionData.value],
  ([loading]) => {
    if (loading) return

    const sub = subscriptionData.value?.data?.subscription ?? null

    if (sub && hasSubscription.value) {
      startDate.value = normalizeIsoDate(sub.start_date)
      periodMonths.value = sub.months != null ? String(sub.months) : '3'
      step3State.value = createStep3StateFromSubscription(sub)
      return
    }

    startDate.value = ''
    periodMonths.value = '3'
    step3State.value = createDefaultStep3State()
  },
  { immediate: true }
)

const step3Delivery = computed({
  get: () => step3State.value.delivery,
  set: (value) => {
    step3State.value = {
      ...step3State.value,
      delivery: value,
    }
  },
})

const step3PayType = computed({
  get: () => step3State.value.payType,
  set: (value) => {
    step3State.value = {
      ...step3State.value,
      payType: normalizePayType(value),
    }
  },
})

const step3Phone = computed({
  get: () => step3State.value.phone,
  set: (value) => {
    step3State.value = {
      ...step3State.value,
      phone: String(value ?? ''),
    }
  },
})

const step3Note = computed({
  get: () => step3State.value.note,
  set: (value) => {
    step3State.value = {
      ...step3State.value,
      note: String(value ?? ''),
    }
  },
})

const step3NotifyDays = computed({
  get: () => step3State.value.notifyDays,
  set: (value) => {
    step3State.value = {
      ...step3State.value,
      notifyDays: String(value ?? ''),
    }
  },
})

const step3Agreement = computed({
  get: () => step3State.value.agreement,
  set: (value) => {
    step3State.value = {
      ...step3State.value,
      agreement: !!value,
    }
  },
})

function calcEndDate(startYmd, months) {
  const iso = normalizeIsoDate(startYmd)
  if (!iso) return ''

  const dt = new Date(`${iso}T00:00:00`)
  dt.setMonth(dt.getMonth() + Number(months || 0))

  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const d = String(dt.getDate()).padStart(2, '0')

  return `${y}-${m}-${d}`
}

const MIN_BLOCKS = 2
const MAX_BLOCKS = 5

const productKey = (p) => p.id ?? p.handle ?? p.product_title
const selectedMap = ref(new Map())

const getQty = (p) => selectedMap.value.get(productKey(p))?.qty ?? 0

const getSubscriptionQty = (product) => {
  const found = subscriptionProducts.value.find((item) => (
    item.shopify_product_id == product.product_id ||
    item.shopify_variant_id == product.product_innerid
  ))

  return Number(found?.amount || 0)
}

const buildSelectedMapFromSubscription = () => {
  const map = new Map()

  subscriptionProducts.value.forEach((item) => {
    const matchedProduct = products.value.find((product) => (
      item.shopify_product_id == product.product_id ||
      item.shopify_variant_id == product.product_innerid
    ))

    if (!matchedProduct) return

    const qty = Number(item.amount || 0)

    if (qty > 0) {
      map.set(productKey(matchedProduct), {
        product: matchedProduct,
        qty,
      })
    }
  })

  selectedMap.value = map
}

const selectedCount = computed(() => {
  let sum = 0
  for (const item of selectedMap.value.values()) sum += item.qty
  return sum
})

const selectedProducts = computed(() => Array.from(selectedMap.value.values()))
const canAddMore = computed(() => selectedCount.value < MAX_BLOCKS)
const canOpenStep2 = computed(() => selectedCount.value >= MIN_BLOCKS)

const canOpenStep3 = computed(() =>
  selectedCount.value >= MIN_BLOCKS &&
  (hasSubscription.value || (!!startDate.value && !!periodMonths.value))
)

const canGoNextBtn = computed(() => {
  if (activeStep.value === 1) return canOpenStep2.value
  if (activeStep.value === 2) return canOpenStep3.value
  if (activeStep.value === 3) return canSubmit.value
  return false
})

function buildPayload({ delivery, payType, phone, note, notifyDays }) {
  const productsPayload = selectedProducts.value.map((item) => ({
    product_id: item.product.product_id,
    variant_id: item.product.product_innerid,
    amount: item.qty,
  }))

  return {
    customer_id: customer.id,
    notify_days: notifyDays ?? 0,
    months: periodMonths.value,
    pay_type: payType,
    start_date: startDate.value,
    end_date: calcEndDate(startDate.value, periodMonths.value),
    phone,
    note,
    delivery_address: [
      delivery.cityLabel,
      delivery.fields?.np_department_label || delivery.fields?.np_department || '',
    ]
      .filter(Boolean)
      .join(', '),
    json: {
      np_city: delivery.city,
      np_province: delivery.province,
      delivery_city_id: delivery.deliveryCityId || '',
      address_type: Number(delivery.type),
      np_delivery_type: delivery.deliveryTypeId,
      np_department: delivery.fields?.np_department || '',
    },
    products: productsPayload,
  }
}

const canSubmit = computed(() => {
  const dep = String(step3State.value.delivery?.fields?.np_department ?? '').trim()
  const phoneDigits = normalizePhoneDigits(String(step3State.value.phone ?? ''))
  const deliveryCityId = String(step3State.value.delivery?.deliveryCityId ?? '').trim()

  return (
    step3State.value.agreement === true &&
    phoneDigits.length >= 10 &&
    String(step3State.value.delivery?.cityLabel ?? '').trim().length > 0 &&
    deliveryCityId.length > 0 &&
    dep.length > 0
  )
})

function getFirstErrorMessage(errors, fields = []) {
  if (!errors || typeof errors !== 'object') return ''

  for (const field of fields) {
    const value = errors[field]
    if (Array.isArray(value)) return String(value[0] || '')
    if (value) return String(value)
  }

  return ''
}

function getSubmitCityError(error) {
  const errors = error?.data?.errors
  return getFirstErrorMessage(errors, [
    'delivery_city_id',
    'city',
    'np_city',
    'json.delivery_city_id',
    'json.np_city',
  ])
}

const handleSubmit = async () => {
  if (isSubscribeDisabled.value) return

  actionType.value = hasSubscription.value ? 'update' : 'create'

  const payload = buildPayload({
    delivery: step3State.value.delivery,
    payType: step3State.value.payType,
    phone: step3State.value.phone,
    note: step3State.value.note,
    notifyDays: step3State.value.notifyDays,
  })
  try {
    await submit(payload)
    await init()
    buildSelectedMapFromSubscription()
    openSuccessModal()
  } catch (e) {
    const cityError = getSubmitCityError(e)
    if (cityError) {
      step3Ref.value?.setCityError?.(cityError)
      await nextTick()
      await scrollToStep(3)
    }

    console.error(e)
  }
}

const isProductModalOpen = ref(false)
const modalProduct = ref(null)

const openProductModal = (product) => {
  modalProduct.value = product
  isProductModalOpen.value = true
}

const closeProductModal = () => {
  isProductModalOpen.value = false
  modalProduct.value = null
}

const onModalAdd = (product) => {
  addBlock(product)
}

const isSuccessModalOpen = ref(false)

const openSuccessModal = () => {
  document.querySelector('body')?.classList.add('overflow-hidden')
  isSuccessModalOpen.value = true

  const eventName = actionType.value === 'create' ? 'subscription_success' : 'subscription_edited'
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: eventName })
}

const closeSuccessModal = () => {
  document.querySelector('body')?.classList.remove('overflow-hidden')
  isSuccessModalOpen.value = false
}

function parseJsonFromElementById(id) {
  const el = document.getElementById(id)
  if (!el) return null

  try {
    return JSON.parse(el.textContent?.trim() || '{}')
  } catch (e) {
    console.error(`[${id}] invalid JSON`, e)
    return null
  }
}

onMounted(async () => {
  const data = parseJsonFromElementById('subscribe-products')
  products.value = Array.isArray(data) ? data : (data?.products ?? [])

  await init()
  buildSelectedMapFromSubscription()

  if (view.value === 'all') await initTom()
})

watch(
  () => [subscriptionProducts.value, products.value],
  ([subProducts, productList]) => {
    if (!subProducts?.length || !productList?.length) return
    buildSelectedMapFromSubscription()
  }
)

const activeStep = ref(1)

const wait = (ms = 0) => new Promise(resolve => setTimeout(resolve, ms))

const waitForLayout = async () => {
  await nextTick()
  await new Promise(resolve => requestAnimationFrame(() => resolve()))
  await new Promise(resolve => requestAnimationFrame(() => resolve()))
  await wait(220)
}

const getStickyOffset = () => {
  const header = document.querySelector('.header')
  return header ? header.getBoundingClientRect().height + 16 : 24
}

const scrollToStep = async (step) => {
  await waitForLayout()

  const stepEl = document.querySelector(`[data-step="${step}"]`)
  if (!stepEl) return

  const y = stepEl.getBoundingClientRect().top + window.scrollY - getStickyOffset()

  window.scrollTo({
    top: Math.max(0, y),
    behavior: 'smooth',
  })
}

const ensureWizardView = async () => {
  if (view.value === 'all') {
    backToWizard()
    await nextTick()
  }
}

const onNextClick = async () => {
  if (isSubscribeDisabled.value) return

  if (activeStep.value === 1) {
    if (!canOpenStep2.value) return

    await ensureWizardView()
    activeStep.value = 2
    await scrollToStep(2)
    return
  }

  if (activeStep.value === 2) {
    await ensureWizardView()

    const valid = await step2Ref.value?.validateStep?.()
    if (!valid) return
    if (!canOpenStep3.value) return

    activeStep.value = 3
    await scrollToStep(3)
    return
  }

  if (activeStep.value === 3) {
    await ensureWizardView()

    const valid = await step3Ref.value?.validateStep?.()
    if (!valid) return

    await handleSubmit()
  }
}

const toggleStep = (step) => {
  if (step === 2 && !canOpenStep2.value) return
  if (step === 3 && !canOpenStep3.value) return
  activeStep.value = activeStep.value === step ? null : step
}

const addBlock = (p) => {
  if (selectedCount.value >= MAX_BLOCKS) return

  const key = productKey(p)
  const currentQty = getQty(p)
  const subscriptionQty = getSubscriptionQty(p)

  if (subscriptionQty > 0 && currentQty >= subscriptionQty) return

  const map = new Map(selectedMap.value)
  const current = map.get(key)

  if (!current) {
    map.set(key, { product: p, qty: 1 })
  } else {
    map.set(key, { product: current.product, qty: current.qty + 1 })
  }

  selectedMap.value = map
}

const removeBlock = (p) => {
  const key = productKey(p)
  const map = new Map(selectedMap.value)
  const current = map.get(key)

  if (!current) return

  const nextQty = current.qty - 1

  if (nextQty <= 0) map.delete(key)
  else map.set(key, { product: current.product, qty: nextQty })

  selectedMap.value = map
}

const clearProduct = (p) => {
  const key = productKey(p)
  const map = new Map(selectedMap.value)
  map.delete(key)
  selectedMap.value = map
}
function createClearedStep3State(phone = '') {
  return {
    delivery: createDefaultDelivery(),
    payType: DEFAULT_PAY_TYPE,
    phone: String(phone || ''),
    note: '',
    notifyDays: '1',
    agreement: false,
  }
}
const clearAll = () => {
  selectedMap.value = new Map()
  activeStep.value = 1

  if (hasSubscription.value) {
    const sub = subscriptionData.value?.data?.subscription

    startDate.value = normalizeIsoDate(sub?.start_date)
    periodMonths.value = sub?.months != null ? String(sub.months) : '3'

    step3State.value = createClearedStep3State(sub?.phone || customer?.phone || '')
    return
  }

  startDate.value = ''
  periodMonths.value = '3'
  step3State.value = createClearedStep3State(customer?.phone || '')
}

const progressPercent = computed(() =>
  Math.min(100, (selectedCount.value / MAX_BLOCKS) * 100)
)

const totalSum = computed(() => {
  let sum = 0

  for (const item of selectedProducts.value) {
    const price = Number(item.product.product_price) || 0
    sum += price * item.qty
  }

  return sum
})
</script>
